import "./BranchSelector.scss";
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAuth0 } from "../../auth/AuthContext";
import { PERMISSIONS } from "../../auth/types";
import { RootState, useThunkDispatch } from "../../store";
import { switchBranch } from "../../store/app/actions";
import TextField from "./TextField";
import { BranchesClient } from "../core-api-client";
import { showDetailedError } from "../../store/notifications/actions";
import { Autocomplete, Option } from "./Autocomplete";
import { useSettings } from "../providers/SettingsProvider";

const sanitizeBranchName = (name: string) => {
    return name
        ?.toLowerCase() // Azure only allows lowercase characters in blob path
        ?.replace(/[^a-z0-9-\.]+/g, "") // Only allow alphanumeric, dot and hyphen
        ?.replace(/(--)+/g, "-") // Double hyphens are out
        ?.match(/[a-z0-9]+.*[a-z0-9]+/g)?.[0] // Remove leading and trailing dots and hyphens
        ?.substring(0, 63); // Limit length to 63
};

function BranchSelector() {
    const activeBranch = useSelector((store: RootState) => store.app.activeBranch);
    const { coreApiUrl, activeBranch: defaultBranch, domainName } = useSettings();
    const dispatch = useThunkDispatch();
    const { effectiveTags } = useAuth0();
    const isDeveloper = effectiveTags?.includes?.(PERMISSIONS.DEVELOPER_TOOLS_ACCESS);
    const [availableBranches, setAvailableBranches] = useState<Option[]>([]);
    const [text, setText] = React.useState(activeBranch);

    useEffect(() => {
        if (!isDeveloper) return;

        new BranchesClient(coreApiUrl)
            .search(undefined, undefined, domainName)
            .then(result => {
                const options = result.map(x => ({ value: sanitizeBranchName(x.name), label: x.name }));
                setAvailableBranches(options);
            })
            .catch(ex => {
                showDetailedError("Failed to load branches.", ex.response);
            });
    }, [isDeveloper, coreApiUrl]);

    const onTextFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setText(e.target.value);
    }, [setText]);

    const onTextFieldBlur = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const input = e.target.value ?? "";

        const newBranch = sanitizeBranchName(input);
        setText(newBranch ?? defaultBranch);

        if (newBranch === activeBranch) return;

        dispatch(switchBranch(newBranch));
    }, [activeBranch, defaultBranch]);

    const onAutocompleteChange = useCallback((_e: React.SyntheticEvent, selected: Option | null) => {
        if (selected.value === activeBranch) return;

        dispatch(switchBranch(selected.value));
    }, [activeBranch]);

    if (!isDeveloper) return null;

    return (
        <div id="branch-selector">
            {
                availableBranches?.length > 0 ?
                    <Autocomplete
                        size="small"
                        fullWidth
                        label="Active Branch"
                        options={availableBranches}
                        value={availableBranches.find(b => b.value === activeBranch)}
                        onChange={onAutocompleteChange}
                        placeholder="Select a branch..."
                        disableClearable
                        disablePortal
                    /> :
                    <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        label="Active Branch"
                        value={text}
                        onChange={onTextFieldChange}
                        onBlur={onTextFieldBlur}
                    />
            }
        </div>
    );
}

export { BranchSelector };